import React, { Fragment } from 'react';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { getNavigationCallCenterButtonTrackingData } from 'Components/widgets/navigation/Navigation.tracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Button } from 'Components/frameworks/button/Button';
import { Text } from 'Components/frameworks/text/Text';
import { TextLink } from 'Components/frameworks/text-link/TextLink';
import { Title } from 'Components/frameworks/title/Title';
import { t } from 'Src/i18n';
import { useRouter } from 'next/router';
import DynamicLink from 'Components/frameworks/dynamic-link/DynamicLink';
import { Icon } from 'Components/frameworks/icon/Icon';
export type NavigationContactProps = {
  contact?: CMSNavigationBarContact;
  hasTel?: boolean;
  isBookingEnginePage: boolean;
};
export const NavigationContact = ({
  contact = {},
  hasTel,
  isBookingEnginePage,
}: NavigationContactProps) => {
  const track = useTracking();
  const {
    description,
    hours,
    phoneNumber = '',
    title = '',
    toll,
    storeLocatorLabel,
    storeLocatorLink,
    whatsAppNumber = '',
    whatsappHours,
  } = contact;
  const link = `tel:${phoneNumber}`;
  const {
    query: { bi: bookingId },
  } = useRouter();

  if (!isBookingEnginePage) {
    return (
      <Fragment>
        <Title className="mb-10" theme="title-sans-n4">
          {title}
        </Title>
        <Text data-testid="NavigationContact-description" text={description} />
        {toll && (
          <p
            className="mb-20 font-sans text-12 leading-normal text-mediumGrey"
            data-testid="NavigationContact-toll"
          >
            {toll}
          </p>
        )}
        {whatsAppNumber !== '' && <p className="font-bold">{t('navigationBar.contact.byPhone')}</p>}
        <Text
          className="mt-10 text-sienna"
          data-testid="NavigationContact-hours"
          text={hours}
          theme="text-sans-n2"
        />
        {phoneNumber && hasTel && (
          <div className="mt-20 flex items-center justify-center">
            <Button
              data-testid="phoneNumber-button"
              colorBg={COLORS.SAFFRON}
              colorBorder={COLORS.SAFFRON}
              iconLeft={{
                name: ICONS.PHONE_BUBBLE,
              }}
              onClick={() => track(getNavigationCallCenterButtonTrackingData(phoneNumber))}
              url={link}
            >
              <span dir="ltr">{phoneNumber}</span>
            </Button>
          </div>
        )}
        {whatsAppNumber !== '' && (
          <React.Fragment>
            <p className="mt-40 font-bold">{t('navigationBar.contact.byWhatsApp')}</p>
            <Text
              className="mt-10 text-sienna"
              data-testid="NavigationContact-hours-whatsApp"
              text={whatsappHours}
              theme="text-sans-n2"
            />
            <div className="mb-40 mt-30 flex items-center justify-center">
              <Button
                colorBg={COLORS.WHITE}
                colorBorder={COLORS.BLACK}
                data-testid="whatsAppCTA"
                iconLeft={{
                  name: ICONS.WHATSAPP,
                  width: '1.715em',
                }}
                onClick={() =>
                  track({
                    event: 'eventGA',
                    categoryGA: 'headerMenu',
                    actionGA: 'header_contact',
                    labelGA: 'whatsapp',
                    valueGA: 0,
                    nonInteraction: 0,
                  })
                }
                url={whatsAppNumber}
              >
                <span dir="ltr">{t('navigationBar.contact.whatsappCTA')}</span>
              </Button>
            </div>
          </React.Fragment>
        )}
        {!!(storeLocatorLabel && storeLocatorLink) && (
          <div className="mt-10 flex justify-center gap-10">
            <Icon name={ICONS.TRIDENT} width="1.5em" />
            <TextLink
              className="text-12 font-bold underline"
              label={storeLocatorLabel}
              onClick={() =>
                track({
                  actionGA: 'header_contact',
                  categoryGA: 'headerMenu',
                  labelGA: 'clickStoreLocator',
                })
              }
              url={storeLocatorLink}
            />
          </div>
        )}
      </Fragment>
    );
  }

  return (
    <Fragment>
      <p className="mb-20 mt-0 font-brand text-18 text-sienna" data-testid="contact-popin-title">
        {t('navigationBar.contact.advice')}
      </p>
      <Text
        data-testid="NavigationContact-description"
        text={t('booking.confirmation.noteReference')}
      />
      <p
        className="mb-20 mt-0 font-brand text-18 text-sienna"
        data-testid="contact-popin-bookingId"
      >
        {bookingId}
      </p>
      <Text
        data-testid="NavigationContact-description"
        text={t('booking.confirmation.callTravelAgent')}
      />
      <div className="flex items-baseline gap-15">
        <Icon className="text-sienna" name={ICONS.PHONE_BUBBLE} />
        <p
          className="mb-10 mt-0 font-brand text-18 text-sienna underline"
          data-testid="contact-popin-phone-number"
        >
          <DynamicLink url={link}>{phoneNumber}</DynamicLink>
        </p>
      </div>
      {toll && (
        <p
          className="font-sans text-12 leading-normal text-mediumGrey"
          data-testid="NavigationContact-toll"
        >
          {toll}
        </p>
      )}
      <Text
        className="text-sienna"
        data-testid="NavigationContact-hours"
        text={hours}
        theme="text-sans-n2"
      />
      {phoneNumber && hasTel && (
        <div className="mt-20 flex items-center justify-center">
          <Button
            iconLeft={{
              name: ICONS.PHONE_BUBBLE,
            }}
            onClick={() => track(getNavigationCallCenterButtonTrackingData(phoneNumber))}
            url={link}
          >
            <span dir="ltr">{phoneNumber}</span>
          </Button>
        </div>
      )}
    </Fragment>
  );
};
